import { useState, useRef, useCallback, useEffect } from "react";
import "@/App.css";
import { ThemeProvider } from "@/lib/ThemeContext";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { UserDataProvider, useUserData } from "@/lib/UserDataContext";
import { GenerationProvider, useGeneration } from "@/lib/GenerationContext";
import { supabase } from "@/lib/supabase";
import { Header } from "@/components/Header";
import { UnifiedInput } from "@/components/UnifiedInput";
import { AudioPlayer } from "@/components/AudioPlayer";
import { GenerationProgress } from "@/components/GenerationProgress";
import { WordScramble } from "@/components/WordScramble";
import { MasteryQuiz } from "@/components/MasteryQuiz";
import { Celebration } from "@/components/Celebration";
import { PostQuizSheru } from "@/components/PostQuizSheru";
import AskPriya from "@/components/AskPriya";
import { InlineMasteryPanel } from "@/components/InlineMasteryPanel";
import Onboarding from "@/components/Onboarding";
import Auth from "@/components/Auth";
import SignUpGate from "@/components/SignUpGate";
import WelcomeBack from "@/components/WelcomeBack";
import BottomNav from "@/components/BottomNav";
import HomeScreen from "@/components/HomeScreen";
import { ErrorState } from "@/components/ErrorState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Profile from "@/components/Profile";
import StoryLibrary from "@/components/StoryLibrary";
import LoadingScreen from "@/components/LoadingScreen";
import SplashScreen from "@/components/SplashScreen";
import ReturnScreen from "@/components/ReturnScreen";
import InstallBanner from "@/components/InstallBanner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { playStoryComplete } from "@/utils/sounds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const removeEmergentBadge = () => {
  const badge = document.getElementById("emergent-badge");
  if (badge) badge.remove();
};

const AppContent = () => {
  // ─── Context hooks ────────────────────────────────────────────
  const {
    user, session, signInWithGoogle, showSignUpGate, setShowSignUpGate, canGenerateStory,
  } = useAuth();
  const {
    generationId, generationStatus, generationError, isGenerating,
    audioUrl, slides, contentPreview, cliffhangerQuestion, nextTopicFromStory,
    startGeneration, resetGeneration, setGenerationError,
  } = useGeneration();
  const {
    streakCount, showStreakCelebration, masteryData, masteryLoading,
    dailyGoals, petMood, setUserData,
    getAuthHeaders, fetchStreak, fetchMastery, fetchDailyGoals, updateDailyGoal, addPetXPRemote,
  } = useUserData();

  // ─── App routing state ────────────────────────────────────────
  const [appState, setAppState] = useState('loading');
  const [showAuth, setShowAuth] = useState(false);
  const [activeTab, setActiveTab] = useState('home');

  // ─── Player / quiz UI flow ────────────────────────────────────
  const [showPlayer, setShowPlayer] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showQuizButton, setShowQuizButton] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);

  // ─── Post-quiz flow ───────────────────────────────────────────
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [showMasteryPanel, setShowMasteryPanel] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [achievementTopic, setAchievementTopic] = useState(null);
  const [lastQuizScore, setLastQuizScore] = useState(null);
  const [nextTopicSuggestion, setNextTopicSuggestion] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState(null);

  // ─── Return screen ────────────────────────────────────────────
  const [showReturnScreen, setShowReturnScreen] = useState(() => {
    const lastSession = localStorage.getItem('you_last_session_at');
    if (!lastSession) return false;
    return (Date.now() - new Date(lastSession).getTime()) / 3_600_000 >= 48;
  });

  // ─── PWA ──────────────────────────────────────────────────────
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // ─── UI / Sheru ───────────────────────────────────────────────
  const [inputMode, setInputMode] = useState("none");
  // TODO: Re-enable when voice input is implemented
  // const [showSpeakToast, setShowSpeakToast] = useState(false);
  const [sheruState, setSheruState] = useState("idle");
  const [sheruMessage, setSheruMessage] = useState(null);

  // ─── Pending auth flow ────────────────────────────────────────
  const [pendingPrompt, setPendingPrompt] = useState("");
  const [pendingContent, setPendingContent] = useState(null);
  const [pendingLanguage, setPendingLanguage] = useState(
    () => localStorage.getItem('you_language') || 'en-IN'
  );

  const handleShowAuth = useCallback(() => setShowAuth(true), []);

  const handleInstallClick = useCallback(async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false);
      localStorage.setItem('you_pwa_installed', 'true');
    }
    setInstallPrompt(null);
  }, [installPrompt]);

  const handleDismissInstall = useCallback(() => {
    setShowInstallBanner(false);
  }, []);
  const prevGenStatusRef = useRef(null);

  // ─── Session state machine ────────────────────────────────────
  const checkUserState = useCallback(async () => {
    try {
      if (session?.user) {
        const [prefsRes, petRes] = await Promise.all([
          supabase.from('user_preferences').select('*').eq('user_id', session.user.id).maybeSingle(),
          supabase.from('user_pet').select('*').eq('user_id', session.user.id).maybeSingle(),
        ]);
        setUserData({ ...(prefsRes.data || {}), pet: petRes.data || null, session });
        setAppState('home_logged_in');
        return;
      }
      if (localStorage.getItem('you_authenticated') === 'true') { setAppState('welcome_back'); return; }
      if (localStorage.getItem('you_onboarding_complete') === 'true') { setAppState('home_anonymous'); return; }
      setAppState('splash');
    } catch (err) {
      console.error('Session check error:', err);
      setAppState('splash');
    }
  }, [session, setUserData]);

  // Init with fallback timer
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setAppState(current => {
        if (current !== 'loading') return current;
        return localStorage.getItem('you_onboarding_complete') === 'true' ? 'home_anonymous' : 'splash';
      });
    }, 5000);
    checkUserState().finally(() => clearTimeout(fallbackTimer));
  }, [checkUserState]);

  // Auth state listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'SIGNED_IN') {
        checkUserState();
        localStorage.removeItem('you_anon_id');
      }
      if (event === 'SIGNED_OUT') {
        setUserData(null);
        resetGeneration();
        resetAppGenerationState();
        setActiveTab('home');
        setAppState('splash');
      }
    });
    return () => subscription.unsubscribe();
  }, [checkUserState, setUserData]);

  // Fallback: if user becomes null (forced by AuthContext signOut), reset to splash
  useEffect(() => {
    if (user === null && appState !== 'splash' && appState !== 'loading') {
      setUserData(null);
      resetGeneration();
      resetAppGenerationState();
      setActiveTab('home');
      setAppState('splash');
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Generation completion side-effect ────────────────────────
  useEffect(() => {
    if (generationStatus === 'completed' && prevGenStatusRef.current !== 'completed') {
      setShowPlayer(true);
      fetchStreak();
    }
    prevGenStatusRef.current = generationStatus;
  }, [generationStatus, fetchStreak]);

  // ─── Misc effects ────────────────────────────────────────────
  useEffect(() => { removeEmergentBadge(); }, []);

  useEffect(() => {
    if (!localStorage.getItem('you_anon_id')) {
      localStorage.setItem('you_anon_id', 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
    }
  }, []);

  useEffect(() => {
    const checkLang = () => {
      const stored = localStorage.getItem('you_language');
      if (stored && stored !== pendingLanguage) setPendingLanguage(stored);
    };
    window.addEventListener('storage', checkLang);
    window.addEventListener('focus', checkLang);
    return () => { window.removeEventListener('storage', checkLang); window.removeEventListener('focus', checkLang); };
  }, [pendingLanguage]);

  useEffect(() => {
    const migrationKey = 'you_pwa_migration_v2';
    if (!localStorage.getItem(migrationKey)) {
      localStorage.removeItem('you_pwa_dismissed');
      localStorage.removeItem('you_install_dismissed');
      localStorage.removeItem('you_pwa_installed');
      localStorage.setItem(migrationKey, 'true');
      console.log('[PWA] Migration v2: cleared old dismiss flags');
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const installed = localStorage.getItem('you_pwa_installed') === 'true';
      if (!isStandalone && !installed) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (isGenerating) { setSheruState("thinking"); setSheruMessage("Let me think... 🤔"); }
    else if (showQuizButton && !showQuiz) { setSheruState("idle"); setSheruMessage("Play with me! 🎯"); }
    else if (showPlayer && !isGenerating) { setSheruState("idle"); setSheruMessage("Time to listen! 🎧"); }
    else { setSheruState("idle"); setSheruMessage(null); }
  }, [isGenerating, showPlayer, showQuizButton, showQuiz]);

  // Refetch daily goals when returning to home screen
  useEffect(() => {
    if (activeTab === 'home' && !showPlayer && !isGenerating && !showQuiz) {
      fetchDailyGoals();
    }
  }, [activeTab, showPlayer, isGenerating, showQuiz, fetchDailyGoals]);

  // Auto-trigger generation after auth with pending content
  useEffect(() => {
    if (user && pendingContent) {
      const content = pendingContent;
      const lang = pendingLanguage;
      setPendingContent(null);
      setPendingLanguage(localStorage.getItem('you_language') || 'en-IN');
      setPendingPrompt("");
      handleGenerateInternal(content, lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ─── Callbacks ────────────────────────────────────────────────
  const fetchQuiz = useCallback(async (genId) => {
    if (!genId) return;
    setQuizLoading(true);
    setShowQuiz(false);
    setQuizQuestions([]);
    try {
      const classGroup = localStorage.getItem('you_class') || 'class_5_6';
      const res = await axios.get(`${API}/quiz/${genId}?class_group=${classGroup}`);
      const qs = res.data.questions || [];
      if (qs.length > 0) { setQuizQuestions(qs); setShowQuiz(true); }
    } catch (err) {
    } finally { setQuizLoading(false); }
  }, []);

  const handleQuizSubmit = useCallback(async (score, total) => {
    try {
      const res = await axios.post(`${API}/quiz/submit`, { generation_id: generationId, score, total }, { headers: getAuthHeaders() });
      fetchMastery();
      return res.data;
    } catch (err) { return null; }
  }, [generationId, getAuthHeaders, fetchMastery]);

  const handleQuizComplete = useCallback((score, wrongAnswersArr, masteryResult) => {
    setLastQuizScore(score);
    setWrongAnswers(wrongAnswersArr || []);
    setSheruState(score >= 3 ? "celebrating" : "idle");
    setSheruMessage(score === 3 ? "You are a champion! 🏆" : score === 2 ? "So close! Great work! 🌟" : "Great try! Go again! 💪");
    setNextTopicSuggestion(masteryResult?.next_topic_suggestion || null);
    setWhatsappMessage(masteryResult?.whatsapp_message || null);
    fetchMastery();
    setShowMasteryPanel(true);
    if (score >= 2) updateDailyGoal('quizzed');
    addPetXPRemote(score === 3 ? 15 : score === 2 ? 10 : score === 1 ? 5 : 2);
    setShowCelebration(true);
    if (masteryResult?.mastery_pct >= 75) {
      setAchievementTopic(contentPreview);
      setTimeout(() => setAchievementTopic(null), 3500);
    }
  }, [fetchMastery, contentPreview, updateDailyGoal, addPetXPRemote]);

  const handleAudioEnded = useCallback(() => {
    setShowQuizButton(true);
    updateDailyGoal('listened');
    playStoryComplete();
  }, [updateDailyGoal]);

  // ─── Reset ALL App-level generation UI state to clean ─────────
  const resetAppGenerationState = useCallback(() => {
    setShowPlayer(false);
    setShowQuiz(false);
    setShowQuizButton(false);
    setShowCelebration(false);
    setShowMasteryPanel(false);
    setInputMode('none');
    setQuizQuestions([]);
    setWrongAnswers([]);
    setAchievementTopic(null);
    setLastQuizScore(null);
    setNextTopicSuggestion(null);
    setWhatsappMessage(null);
    setPendingContent(null);
  }, []);

  // ─── Start fresh generation: reset everything, then generate ──
  const handleGenerateInternal = (content, language, isAnonymous = false) => {
    resetAppGenerationState();
    startGeneration(content, { isAnonymous });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleGenerate = useCallback((content, language) => {
    if (content.type === "text") setPendingPrompt(content.text);
    if (!canGenerateStory()) {
      setPendingContent(content);
      setPendingLanguage(language);
      setShowSignUpGate(true);
      return;
    }
    handleGenerateInternal(content, language, !user);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user, canGenerateStory, setShowSignUpGate]);

  const handleCelebrationNext = useCallback((suggestedTopic) => {
    resetGeneration();
    resetAppGenerationState();
    if (suggestedTopic) {
      handleGenerateInternal({ type: 'text', text: suggestedTopic }, localStorage.getItem('you_language') || 'te');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetGeneration, resetAppGenerationState]);

  const handleGenerateFromTopic = useCallback((topic) => {
    handleGenerate({ type: 'text', text: topic }, pendingLanguage);
  }, [pendingLanguage, handleGenerate]);

  const handleGenerateFromTopicAndGoHome = useCallback((topic) => {
    if (topic) handleGenerate({ type: 'text', text: topic }, pendingLanguage);
    setActiveTab('home');
  }, [pendingLanguage, handleGenerate]);

  const handleNextTopic = useCallback((topicText) => {
    setShowQuiz(false);
    setShowMasteryPanel(false);
    setLastQuizScore(null);
    setWrongAnswers([]);
    setNextTopicSuggestion(null);
    setWhatsappMessage(null);
    handleGenerate({ type: "text", text: topicText }, pendingLanguage);
  }, [pendingLanguage, handleGenerate]);

  // TODO: Re-enable when voice input is implemented
  // const handleSpeakClick = () => {
  //   setShowSpeakToast(true);
  //   setTimeout(() => setShowSpeakToast(false), 2500);
  // };

  // ─── Render ───────────────────────────────────────────────────
  if (appState === 'loading') return <LoadingScreen />;
  if (showAuth) return <Auth onSuccess={() => { setShowAuth(false); checkUserState(); }} />;
  if (appState === 'splash') return <SplashScreen onGetStarted={() => setAppState('onboarding')} onSignIn={() => setShowAuth(true)} />;
  if (appState === 'onboarding') return <Onboarding onComplete={() => { localStorage.setItem('you_onboarding_complete', 'true'); setAppState('home_anonymous'); }} />;
  if (appState === 'welcome_back') return <WelcomeBack onDifferentAccount={() => { localStorage.removeItem('you_authenticated'); setAppState('splash'); }} />;
  const hasSupabaseSession = Object.keys(localStorage).some(k => k.startsWith('sb-') && k.includes('auth-token'));
  if (appState === 'home_anonymous' && showSignUpGate && !user && !hasSupabaseSession) {
    return <SignUpGate onSkip={() => { localStorage.setItem('you_signup_gate_passed', 'true'); setShowSignUpGate(false); }} />;
  }
  if (showReturnScreen && localStorage.getItem('you_onboarding_complete') === 'true') {
    return <ReturnScreen onDismiss={() => setShowReturnScreen(false)} />;
  }

  const showAnonymousPaywall = generationError === 'ANONYMOUS_LIMIT_REACHED' && !user;

  return (
    <div className="min-h-screen notebook-bg relative flex flex-col max-w-[720px] mx-auto" data-testid="app-container">
      <Header streakCount={streakCount} masteryData={masteryData} onShowAuth={handleShowAuth} onLogoClick={() => {
        setActiveTab('home');
        setShowPlayer(false);
        setShowQuiz(false);
        setShowQuizButton(false);
        setShowMasteryPanel(false);
        setShowCelebration(false);
        window.scrollTo(0, 0);
      }} />
      {/* Spacer for fixed header */}
      <div className="header-spacer" style={{ height: 56, flexShrink: 0 }} />

      <main className="relative z-10 flex-1 flex flex-col">
        {activeTab === 'ask-priya' && (
          <AskPriya
            user={user}
            language={pendingLanguage}
            board={localStorage.getItem('you_board') || 'CBSE'}
            classLevel={localStorage.getItem('you_class') || 'class_5_6'}
            onGenerateStory={(topic) => {
              setActiveTab('home');
              handleGenerate({ type: 'text', text: topic }, pendingLanguage);
            }}
            onAsked={() => updateDailyGoal('asked')}
          />
        )}
        {activeTab === 'stories' && (
          <div>
            <StoryLibrary user={user} masteryTopics={masteryData} onGenerateStory={handleGenerateFromTopicAndGoHome} />
          </div>
        )}
        {activeTab === 'me' && (
          <div>
            <Profile user={user} masteryTopics={masteryData} onSignIn={signInWithGoogle} onLanguageChange={setPendingLanguage} />
          </div>
        )}
        {activeTab === 'home' && <section className={`flex-1 flex flex-col items-center px-4 pt-6 ${showCelebration ? 'pb-6' : 'pb-36'} mx-auto w-full gap-5`} style={{ maxWidth: 720 }} data-testid="hero-section">
          {!showPlayer && !isGenerating && !showQuiz && (
            showAnonymousPaywall ? <SignUpGate onSkip={() => { setGenerationError(null); }} onSignIn={signInWithGoogle} /> : (
              <HomeScreen user={user} onGenerateStory={handleGenerateFromTopic} masteryTopics={masteryData} onSnapClick={() => setInputMode('snap')} dailyGoals={dailyGoals} petMood={petMood} />
            )
          )}

          <AnimatePresence>
            {inputMode === 'snap' && !showPlayer && !isGenerating && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} className="w-full overflow-hidden">
                <UnifiedInput onGenerate={handleGenerate} disabled={isGenerating} forceMode="snap" pendingPrompt={pendingPrompt} setPendingPrompt={setPendingPrompt} />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {isGenerating && generationStatus && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="w-full">
                <WordScramble subject={null} topicText={pendingPrompt} />
                <GenerationProgress status={generationStatus} topic={pendingPrompt} />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {generationError && !showAnonymousPaywall && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full" data-testid="generation-error">
                <ErrorState
                  type={
                    generationError.includes('blocked') || generationError.includes('doesn') ? 'topic_blocked'
                    : generationError.includes('breather') || generationError.includes('Slow down') ? 'rate_limited'
                    : generationError.includes('internet') || generationError.includes('network') ? 'no_internet'
                    : generationError.includes('failed') ? 'generation_failed'
                    : 'generic'
                  }
                  onRetry={() => { resetGeneration(); resetAppGenerationState(); }}
                  onDismiss={() => { resetGeneration(); resetAppGenerationState(); }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showPlayer && generationId && !showQuiz && !showCelebration && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="w-full">
                <AudioPlayer generationId={generationId} audioUrl={audioUrl} slides={slides} contentPreview={contentPreview} onClose={() => setShowPlayer(false)} onAudioEnded={handleAudioEnded} />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showQuizButton && !showQuiz && !quizLoading && !showCelebration && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="w-full">
                <button onClick={() => { setShowQuizButton(false); fetchQuiz(generationId); }} style={{ width: '100%', height: 56, borderRadius: 16, background: '#448AFF', color: '#fff', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                  Test Yourself! 🎯
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {quizLoading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm text-muted-foreground text-center py-2">
                Preparing your quiz...
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showQuiz && quizQuestions.length > 0 && !showCelebration && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="w-full" style={{ minHeight: '100vh' }}>
                <MasteryQuiz questions={quizQuestions} user={user} onSubmit={handleQuizSubmit} onComplete={handleQuizComplete} />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showCelebration && lastQuizScore !== null && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="w-full">
                <Celebration topic={contentPreview} score={lastQuizScore} totalQuestions={3} petType={localStorage.getItem('you_pet_type') || 'panda'} petName={localStorage.getItem('you_pet_name') || 'Your pet'} cliffhangerQuestion={cliffhangerQuestion} nextTopic={nextTopicFromStory} onNext={handleCelebrationNext} />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {!showCelebration && showQuiz && quizQuestions.length > 0 && showMasteryPanel && lastQuizScore !== null && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: 0.3 }} className="w-full">
                <PostQuizSheru score={lastQuizScore} wrongAnswers={wrongAnswers} topic={contentPreview} nextTopicSuggestion={nextTopicSuggestion} whatsappMessage={whatsappMessage} onNextTopic={handleNextTopic} />
              </motion.div>
            )}
          </AnimatePresence>


          <AnimatePresence>
            {!showCelebration && showMasteryPanel && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: 0.5 }} className="w-full">
                <InlineMasteryPanel masteryData={masteryData} loading={masteryLoading} isAnonymous={!user} />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {!showCelebration && showMasteryPanel && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ delay: 0.8 }} className="w-full">
                <button
                  onClick={() => { setShowQuiz(false); setShowPlayer(false); setShowQuizButton(false); setShowMasteryPanel(false); setInputMode("none"); setWrongAnswers([]); setLastQuizScore(null); setSheruState("idle"); setSheruMessage(null); }}
                  style={{ width: '100%', padding: '12px 0', borderRadius: 12, background: 'transparent', border: '1.5px solid #448AFF', color: '#448AFF', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                >
                  ✨ Generate Another Story
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </section>}
      </main>

      <AnimatePresence>
        {achievementTopic && (
          <motion.div initial={{ opacity: 0, y: -60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -60 }} transition={{ type: "spring", stiffness: 200, damping: 25 }} className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-[#FFD740] text-[#1A1A2E] px-6 py-3 rounded-full shadow-none font-heading font-bold text-sm max-w-[90vw] text-center">
            🏆 {achievementTopic.slice(0, 40)}{achievementTopic.length > 40 ? "…" : ""} added to your achievements!
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showStreakCelebration && (
          <motion.div initial={{ opacity: 0, y: 50, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.9 }} className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#448AFF] text-white px-6 py-3 rounded-full shadow-none font-heading font-semibold text-sm">
            🔥 {streakCount} day streak! Keep going!
          </motion.div>
        )}
      </AnimatePresence>

      {/* TODO: Re-enable when voice input is implemented
      <AnimatePresence>
        {showSpeakToast && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.9 }} className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-card border shadow-lg px-5 py-3 rounded-2xl font-body text-sm text-foreground">
            🎤 Voice input coming soon!
          </motion.div>
        )}
      </AnimatePresence>
      */}

      {showInstallBanner && installPrompt && (
        <div style={{ position: 'fixed', bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))', left: 0, right: 0, zIndex: 99, maxWidth: 720, margin: '0 auto' }}>
          <InstallBanner onInstall={handleInstallClick} onDismiss={handleDismissInstall} />
        </div>
      )}

      {!showPlayer && !showQuiz && <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <UserDataProvider>
          <GenerationProvider>
            <ErrorBoundary>
              <AppContent />
            </ErrorBoundary>
          </GenerationProvider>
        </UserDataProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
